import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { CustomHttpClient } from './custom-http-client.service';
import { TabsService } from './tabs.service';
import { TrackingService } from './tracking.service';
import { UserService } from './user.service';
import { Globals } from '../globals';

export interface PendingTimeBlock {
  ids: number[];
  patient_name: string;
  programme_type: string;
  session_seconds: number;
  logged_seconds: number;
  minutes: number;
  month: string;
  captured_at: string;
  locked: boolean;
}

/**
 * Saves the unlogged enrolled-patient session time on the server as drafts, so it survives session timeout, logout,
 * browser close, crash and offline. Best effort: failures are swallowed and never block the app, the timers or
 * logout. It never logs time itself, the user reviews the drafts in the recovery popup after the next login.
 * Uses CustomHttpClient (not HttpTokenService) so a failed background call can not trigger the "not authorized"
 * popup / redirect.
 */
@Injectable({
  providedIn: 'root'
})
export class PendingTimeService {
  static readonly CHECKPOINT_MS = 120 * 1000;     // while a timer keeps running
  static readonly PAUSE_DEBOUNCE_MS = 1500;       // after idle / tab switch / hidden
  static readonly LOGOUT_FLUSH_TIMEOUT_MS = 8000; // logout never waits longer than this
  static readonly REQUEST_TIMEOUT_MS = 10000;     // how long a background write is awaited
  static readonly USER_ACTION_TIMEOUT_MS = 60000; // log / discard clicked by the user
  static readonly RETRY_ATTEMPTS = 6;
  static readonly RETRY_DELAY_MS = 10000;
  static readonly FLAG_CACHE_KEY = 'pendingTimeEnabled';

  public enabled = false;
  public blocks$ = new BehaviorSubject<PendingTimeBlock[]>([]);

  private started = false;
  private subs = new Subscription();
  private intervalId: any = null;
  private lastSignature = '';
  private queue: Promise<any> = Promise.resolve();
  private pageHideHandler = () => this.flushOnPageHide();

  constructor(private http: CustomHttpClient, private tabs: TabsService, private tracking: TrackingService,
              private userService: UserService, private ngZone: NgZone, private globals: Globals) {
  }

  onUserReady(): void {
    if (!this.isPracticeUser() || this.started) {
      return;
    }
    this.started = true;
    // start with the last known setting (unknown = on) so a slow settings answer leaves no unprotected window
    if (this.cachedFlag() !== false) {
      this.activate();
    }
    this.loadRecovery();
    this.withRetry(() => this.http.get('pending_time/settings').toPromise()).then(settings => {
      if (!this.started) {
        return;
      }
      const on = !!(settings && settings.enabled);
      this.cacheFlag(on);
      on ? this.activate() : this.deactivate();
    }).catch(() => {
      // server not reachable: keep the current state
    });
  }

  private activate(): void {
    if (!this.enabled) {
      this.enabled = true;
      this.startTracking();
    }
  }

  private deactivate(): void {
    if (this.enabled) {
      this.stopTracking();
      this.enabled = false;
    }
  }

  private cachedFlag(): boolean | null {
    try {
      const value = localStorage.getItem(PendingTimeService.FLAG_CACHE_KEY);
      return value === '1' ? true : value === '0' ? false : null;
    } catch (e) {
      return null;
    }
  }

  private cacheFlag(on: boolean): void {
    try {
      localStorage.setItem(PendingTimeService.FLAG_CACHE_KEY, on ? '1' : '0');
    } catch (e) {
      // storage not available
    }
  }

  onLogout(): void {
    this.stopTracking();
    this.enabled = false;
    this.started = false;
    this.lastSignature = '';
    this.blocks$.next([]);
  }

  /** Retried on failure and never cancelled by a timeout, otherwise drafts could stay unnoticed until next login. */
  loadRecovery(): Promise<void> {
    return this.withRetry(() => this.http.get('pending_time').toPromise())
      .then((blocks: PendingTimeBlock[]) => {
        this.ngZone.run(() => this.blocks$.next(Array.isArray(blocks) ? blocks : []));
      }).catch(() => {
      });
  }

  logBlock(block: PendingTimeBlock, minutes: number, description: string): Promise<any> {
    return this.enqueue(() => this.http.post('pending_time/log',
      {ids: block.ids, minutes: minutes, description: description}).toPromise(),
      PendingTimeService.USER_ACTION_TIMEOUT_MS).then(res => {
      this.removeBlock(block);
      return res;
    });
  }

  discardBlock(block: PendingTimeBlock, note?: string): Promise<any> {
    return this.enqueue(() => this.http.post('pending_time/discard',
      {ids: block.ids, note: note || 'user_discarded'}).toPromise(),
      PendingTimeService.USER_ACTION_TIMEOUT_MS).then(res => {
      this.removeBlock(block);
      return res;
    });
  }

  private removeBlock(block: PendingTimeBlock): void {
    this.blocks$.next(this.blocks$.value.filter(b => b !== block));
  }

  /** The close-tab "Remaining Log Time" popup handled this tab. */
  settle(draftKey: string): void {
    if (!this.enabled || !draftKey) {
      return;
    }
    this.enqueue(() => this.http.post('pending_time/settle', {draft_key: draftKey}).toPromise()).catch(() => {
    });
  }

  private startTracking(): void {
    if (this.userService.beforeLogoutHooks.indexOf(this.beforeLogout) === -1) {
      this.userService.beforeLogoutHooks.push(this.beforeLogout);
    }
    this.ngZone.runOutsideAngular(() => {
      this.intervalId = setInterval(() => this.checkpoint('checkpoint'), PendingTimeService.CHECKPOINT_MS);
      window.addEventListener('pagehide', this.pageHideHandler);
    });
    // a timer was paused (idle, other tab, hidden browser tab)
    this.subs.add(this.tabs.startStopEnrollmentObjectObservable.pipe(
      filter(item => !!item && item['status'] === 'inactive'),
      debounceTime(PendingTimeService.PAUSE_DEBOUNCE_MS)
    ).subscribe(() => this.checkpoint('paused')));
    // a tab was closed: keep its time on the server until the popup is answered
    this.subs.add(this.tracking.closeTabCall.subscribe(item => this.checkpointClosedTab(item)));
  }

  private stopTracking(): void {
    this.subs.unsubscribe();
    this.subs = new Subscription();
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    window.removeEventListener('pagehide', this.pageHideHandler);
    const i = this.userService.beforeLogoutHooks.indexOf(this.beforeLogout);
    if (i > -1) {
      this.userService.beforeLogoutHooks.splice(i, 1);
    }
  }

  /** Registered in UserService.beforeLogoutHooks: saves everything before the token is invalidated. */
  private beforeLogout = (reason?: string): Promise<any> => {
    if (!this.enabled) {
      return Promise.resolve();
    }
    const enrolments = this.collectEnrolments();
    const entries = this.buildEntries(enrolments);
    const finish = () => this.resetEnrolments(enrolments);
    if (!entries.length) {
      finish();
      return Promise.resolve();
    }
    // sent directly (not queued behind slow checkpoints) and awaited, so sign-out can not overtake it
    return this.send(entries, true, reason || 'logout', true).then(finish, finish);
  }

  private checkpoint(reason: string): Promise<any> {
    if (!this.enabled) {
      return Promise.resolve();
    }
    const entries = this.buildEntries(this.collectEnrolments());
    if (!entries.length) {
      return Promise.resolve();
    }
    const signature = JSON.stringify(entries.map(e => [e.draft_key, e.session_seconds, e.logged_seconds]));
    if (signature === this.lastSignature) {
      return Promise.resolve();
    }
    return this.send(entries, false, reason).then(() => {
      this.lastSignature = signature;
    }, () => {
    });
  }

  private checkpointClosedTab(item: any): void {
    if (!this.enabled || !item) {
      return;
    }
    if (!item.draft_key) {
      item.draft_key = this.newKey(); // closed before its first checkpoint; the popup settles this key
    }
    const entry = {
      draft_key: item.draft_key,
      programme_enrolment_id: item.id,
      programme_type: item.programme_type || '',
      session_seconds: this.minutesToSeconds(item.session_time),
      logged_seconds: this.minutesToSeconds(item.logged_time)
    };
    if (entry.session_seconds > 0) {
      this.send([entry], true, 'tab_closed').catch(() => {
      });
    }
  }

  private send(entries: any[], final: boolean, reason: string, direct = false): Promise<any> {
    const put = () => this.http.put('pending_time/checkpoint',
      {entries: entries, final: final, reason: reason}).toPromise();
    const request = direct ? put() : this.enqueue(put);
    if (!final) {
      return request;
    }
    return Promise.race([request, new Promise(resolve =>
      setTimeout(resolve, PendingTimeService.LOGOUT_FLUSH_TIMEOUT_MS))]);
  }

  /** Writes run one after another so a settle can not overtake the checkpoint of the same tab. */
  private enqueue(operation: () => Promise<any>, waitMs = PendingTimeService.REQUEST_TIMEOUT_MS): Promise<any> {
    const run = this.queue.then(() => this.withTimeout(operation(), waitMs), () => this.withTimeout(operation(), waitMs));
    this.queue = run.catch(() => {
    });
    return run;
  }

  /** Retries a failed request a few times; stops at logout. */
  private withRetry<T>(request: () => Promise<T>, attemptsLeft = PendingTimeService.RETRY_ATTEMPTS): Promise<T> {
    return request().catch(error => {
      if (attemptsLeft <= 1 || !this.started) {
        return Promise.reject(error);
      }
      return new Promise(resolve => setTimeout(resolve, PendingTimeService.RETRY_DELAY_MS))
        .then(() => this.started ? this.withRetry(request, attemptsLeft - 1) : Promise.reject(error));
    });
  }

  /** Stops waiting after waitMs; the request keeps running. */
  private withTimeout(promise: Promise<any>, waitMs = PendingTimeService.REQUEST_TIMEOUT_MS): Promise<any> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('timeout')), waitMs);
      promise.then(r => {
        clearTimeout(timer);
        resolve(r);
      }, e => {
        clearTimeout(timer);
        reject(e);
      });
    });
  }

  /** Tab closing or reloading: keepalive lets the request finish after the page is gone. */
  private flushOnPageHide(): void {
    if (!this.enabled) {
      return;
    }
    const entries = this.buildEntries(this.collectEnrolments());
    if (!entries.length) {
      return;
    }
    try {
      const headers: any = {'Content-Type': 'application/json'};
      const names = {'access-token': 'accessToken', 'client': 'client', 'uid': 'uid', 'token-type': 'tokenType', 'expiry': 'expiry'};
      Object.keys(names).forEach(h => {
        const value = localStorage.getItem(names[h]);
        if (value) {
          headers[h] = value;
        }
      });
      fetch(`${this.globals.baseApiUrl}/api/v1/pending_time/checkpoint`, {
        method: 'PUT', keepalive: true, headers: headers,
        body: JSON.stringify({entries: entries, final: true, reason: 'page_close'})
      }).catch(() => {
      });
    } catch (e) {
      // nothing else can be done while the page is closing
    }
  }

  private collectEnrolments(): any[] {
    const found: any[] = [];
    const directory = this.tabs.tabs_directory || {};
    Object.keys(directory).forEach(type => {
      (directory[type] || []).forEach(tab => {
        const enrolment = tab && tab.dependencies && tab.dependencies['enrolment'];
        if (enrolment && enrolment.id && found.indexOf(enrolment) === -1) {
          found.push(enrolment);
        }
      });
    });
    return found;
  }

  private buildEntries(enrolments: any[]): any[] {
    return enrolments.map(e => ({
      draft_key: this.ensureKey(e),
      programme_enrolment_id: e.id,
      programme_type: e.selected_enrollment_type || '',
      session_seconds: Math.max(0, Math.floor(Number(e.session_time) || 0)),
      logged_seconds: this.minutesToSeconds(this.tracking.getLoggedEnrollmentTime(e.id))
    })).filter(e => e.session_seconds > 0);
  }

  /** After the final flush the time lives on the server; the next session starts from zero. */
  private resetEnrolments(enrolments: any[]): void {
    enrolments.forEach(e => {
      try {
        e.unsubscribe();
      } catch (err) {
        // timer was never started
      }
      e.session_time = 0;
      e.draft_key = null;
      this.tracking.deleteEnrollmentObjectLog(e.id);
    });
  }

  private ensureKey(enrolment: any): string {
    if (!enrolment.draft_key) {
      enrolment.draft_key = this.newKey();
    }
    return enrolment.draft_key;
  }

  private newKey(): string {
    const c: any = (window as any).crypto;
    if (c && c.randomUUID) {
      return c.randomUUID();
    }
    return 'k' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  private minutesToSeconds(minutes: any): number {
    const value = parseFloat(String(minutes));
    return isNaN(value) || value < 0 ? 0 : Math.round(value * 60);
  }

  private isPracticeUser(): boolean {
    const user = this.userService.current_user;
    return !!user && user.userType === 'practice';
  }
}
